/** Rendering helpers shared by the log list, the drawer and the tables. */

const TWO = (n: number): string => String(n).padStart(2, "0");

/**
 * Whether timestamps render in UTC or the viewer's local zone.
 *
 * Module state rather than a prop threaded through every component that shows a time. It is set once
 * from Settings and read on render; the alternative is passing a mode argument through the log list,
 * the drawer, the voice table and the audit table for a value that is the same everywhere.
 */
let timeMode: "utc" | "local" = "utc";

export function setTimeMode(mode: "utc" | "local"): void {
  timeMode = mode;
}

export function getTimeMode(): "utc" | "local" {
  return timeMode;
}

/**
 * `dd/MM/yy HH:mm:ss`.
 *
 * UTC by default, matching DebugLogEntry.format() on the plugin side exactly. Staff read the same
 * events in `/debug` in game and here, often at the same time while working out what happened, and
 * two renderings of one timestamp is a reliable way to make people mistrust both.
 */
export function formatTimestamp(millis: number): string {
  const d = new Date(millis);
  const [day, month, year, hour, minute, second] =
    timeMode === "utc"
      ? [d.getUTCDate(), d.getUTCMonth() + 1, d.getUTCFullYear(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
      : [d.getDate(), d.getMonth() + 1, d.getFullYear(), d.getHours(), d.getMinutes(), d.getSeconds()];

  return (
    `${TWO(day)}/${TWO(month)}/${TWO(year % 100)} ` +
    `${TWO(hour)}:${TWO(minute)}:${TWO(second)}`
  );
}

/** Just the clock portion, for dense feeds where the date is implied by "recent". */
export function formatClock(millis: number): string {
  const d = new Date(millis);
  return timeMode === "utc"
    ? `${TWO(d.getUTCHours())}:${TWO(d.getUTCMinutes())}`
    : `${TWO(d.getHours())}:${TWO(d.getMinutes())}`;
}

/**
 * Auction prices, which run to hundreds of millions.
 *
 * Grouped and abbreviated together — `$500,000,000 (500M)` — because the digits are what staff
 * compare against other listings while the abbreviation is what they actually read. At this
 * magnitude the full number alone is genuinely hard to size up at a glance.
 */
export function formatMoney(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "unknown";
  const exact = `$${Math.round(value).toLocaleString()}`;
  if (Math.abs(value) >= 1_000_000_000) return `${exact} (${(value / 1_000_000_000).toFixed(1)}B)`;
  if (Math.abs(value) >= 1_000_000) return `${exact} (${Math.round(value / 1_000_000)}M)`;
  if (Math.abs(value) >= 10_000) return `${exact} (${Math.round(value / 1_000)}K)`;
  return exact;
}

/** Thousands separators without dragging in Intl formatting options everywhere. */
export function formatCount(n: number): string {
  return n.toLocaleString();
}

/** Compact relative time for the drawer, where "3d ago" reads faster than a full timestamp. */
export function formatRelative(millis: number | null): string {
  if (millis === null) return "unknown";
  const seconds = Math.round((Date.now() - millis) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export function formatDuration(millis: number | null): string {
  if (millis === null) return "permanent";
  const remaining = millis - Date.now();
  if (remaining <= 0) return "expired";
  const hours = Math.round(remaining / 3_600_000);
  if (hours < 48) return `${hours}h left`;
  return `${Math.round(hours / 24)}d left`;
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

/**
 * A stable colour per log type.
 *
 * Hand-picked rather than hashed: a hash would drift two types onto neighbouring hues and the whole
 * point of the colour is to be able to pick PUNISHMENT out of a scrolling wall of COMMAND at a
 * glance. Roughly grouped by meaning — money warm, moderation red, movement blue.
 */
export const TYPE_COLORS: Readonly<Record<string, string>> = {
  PAYMENT: "#6dd58c",
  ORDER: "#4bbf9a",
  AUCTION: "#39b5a8",
  WAGERS: "#d9a441",
  TPA: "#5aa9e6",
  SERVER_MOVEMENT: "#4f8fd0",
  HOME: "#7aa5d2",
  DEATH: "#b56576",
  KILL: "#e0697a",
  COMMAND: "#8b95a7",
  CHAT: "#a9b4c4",
  CHAT_FILTER: "#e0863a",
  PUNISHMENT: "#f2545b",
  TEAM: "#a884d8",
  ADMIN: "#cf91ff",
  // Deliberately the same red as PUNISHMENT: a dupe row is the same class of event to a staff
  // member scanning the list, and giving it its own hue would make it read as unrelated.
  DUPE: "#f2545b",
  // Its own vivid orange-red rather than DUPE's red: a stash is a place staff have to travel to, not
  // an incident the system already handled, and it should jump out of the list as such.
  SPAWNER_STASH: "#ff5a1f",
  // Muted on purpose: admin-only and very noisy, so it must not drown out the types around it.
  WAGER_DEBUG: "#8a80a8",
};

export function typeColor(type: string): string {
  return TYPE_COLORS[type] ?? "#8b95a7";
}

/** Display names that differ from the lowercased type. Anything not listed, including a type this
 *  build has never heard of, shows as its raw name. */
const TYPE_LABELS: Readonly<Record<string, string>> = {
  SPAWNER_STASH: "spawner stash",
  WAGER_DEBUG: "wager debug",
};

export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type.toLowerCase();
}

/**
 * Removes the sender's name from the front of a message, since the list now shows it in its own
 * column.
 *
 * Nearly every message the plugin writes is prose that begins with the player: "Kazoo paid Steve
 * $500", "MasnyAlesso ran: /sell", "Griefer was banned by Admin". With a dedicated sender column
 * that prefix is said twice on every row, and it pushes the part that actually differs off to the
 * right where it is harder to scan.
 *
 * Only an exact leading match is stripped, and only when followed by a space — a chat message that
 * merely happens to mention the sender mid-sentence is left completely alone.
 */
export function stripLeadingPlayer(message: string, player: string | null): string {
  if (!player) return message;
  const prefix = `${player.toLowerCase()} `;
  if (!message.toLowerCase().startsWith(prefix)) return message;
  return message.slice(player.length + 1);
}
